import { inject, Injectable } from '@angular/core';
import { first, firstValueFrom } from 'rxjs';
import { DialogData } from '../../common/components/dialog/dialog.component';
import { KnownMembership } from 'matrix-js-sdk/lib/@types/membership.js';
import { MatrixService } from '../matrix/matrix.service';
import { DialogService } from '../dialog/dialog.service';
import { RoomHelperService } from '../room/room-helper.service';
import { RoomListAccessService } from '../room-list/room-list-access.service';
import { AccountService } from '../account/account.service';
import { KickTestIds } from './kick.test-ids';
import { RoomPowerLevelService } from '../room-power-level/room-power-level.service';

@Injectable({
  providedIn: 'root'
})
/**
 * Kick-Funktionalität aus dem lten RoomService
 * Begründung: AFO?
 */
export class KickService {
  private readonly matrixService = inject(MatrixService);
  private readonly roomListAccessService = inject(RoomListAccessService);
  private readonly roomPowerLevelService = inject(RoomPowerLevelService);
  private readonly roomHelperService = inject(RoomHelperService);
  private readonly dialogService = inject(DialogService);
  private readonly accountService = inject(AccountService);

  /**
   * Prüft, ob der angemeldete User einen bestimmten anderen User aus dem Chat entfernen
   *  @param roomId
   *  @param memberMxId
   */
  isKickable(roomId: string, memberMxId: string): boolean {
    if (this.roomHelperService.getRoomMembership(roomId) === KnownMembership.Leave) {
      return false;
    }

    const userId = this.accountService.getMyMxId();

    const user = this.roomListAccessService.getModelRoomMember(roomId, userId);
    if (!user) {
      return false;
    }

    const currentModelRoom = this.roomListAccessService.getCurrentModelRoom();

    if (!currentModelRoom) {
      return false;
    }

    return userId != memberMxId && this.roomPowerLevelService.canDoAction('kick', currentModelRoom.powerLevels, userId);
  }

  /**
   * Entfernt einen User aus dem Chat
   */
  async kick(roomId: string, memberMxId: string): Promise<void> {
    await this.matrixService.client.kick(roomId, memberMxId);
  }

  /**
   * Dialog für einen einzelnen Kick
   */
  async openKickDialog(roomId: string, memberMxId: string): Promise<void> {
    const member = this.roomListAccessService.getModelRoomMember(roomId, memberMxId);
    if (!member) {
      throw new Error(`Member ${memberMxId} not found in room ${roomId}`);
    }
    const dialogData: DialogData = {
      title: 'TEILNEHMER ENTFERNEN',
      svgIcon: 'circle-warning',
      message: `Sind Sie sicher, dass Sie ${member.mxid} aus diesem Chat entfernen wollen? Der Teilnehmer kann danach keine Nachrichten mehr senden. Möchten Sie den Teilnehmer trotzdem entfernen?`,
      confirmText: 'Teilnehmer entfernen',
      cancelText: 'Abbrechen',
      cancelDataTestId: KickTestIds.buttonCancelKickmember,
      confirmDataTestId: KickTestIds.buttonConfirmKickmember,
      dialogActionTestId: KickTestIds.dialogKickmember,
      dialogContentTestId: KickTestIds.dialogKickmemberContent
    };
    const confirmed = await firstValueFrom(this.dialogService.openWarningDialog(dialogData).pipe(first()));
    if (confirmed) {
      await this.kick(roomId, memberMxId);
    }
  }

  /**
   * Dialog für Bulk-Kick (alle Mitglieder)
   */
  async openKickRoomMembersDialog(roomId: string, memberMxIds: string[]): Promise<void> {
    const memberCount = memberMxIds.length;
    const message =
      memberCount === 1
        ? `Sind Sie sicher, dass Sie das einzige verbleibende Mitglied aus diesem Chat entfernen wollen?`
        : `Sind Sie sicher, dass Sie alle ${memberCount} Mitglieder aus diesem Chat entfernen wollen?`;
    const dialogData: DialogData = {
      title: 'MITGLIEDER ENTFERNEN',
      svgIcon: 'circle-warning',
      message: message,
      confirmText: 'Alle entfernen',
      cancelText: 'Abbrechen',
      cancelDataTestId: KickTestIds.buttonCancelBulkKick,
      confirmDataTestId: KickTestIds.buttonConfirmBulkKick,
      dialogActionTestId: KickTestIds.dialogBulkKick,
      dialogContentTestId: KickTestIds.dialogBulkKickContent
    };
    const confirmed = await firstValueFrom(this.dialogService.openWarningDialog(dialogData).pipe(first()));
    if (confirmed) {
      await this.kickRoomMembers(roomId, memberMxIds);
    }
  }

  /**
   * Entfernt mehrere User aus dem Chat
   */
  async kickRoomMembers(roomId: string, memberMxIds: string[]): Promise<void> {
    for (const memberMxId of memberMxIds) {
      await this.kick(roomId, memberMxId);
    }
  }
}
